import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./Component/CSS/ReadMoreNonFictionalComponent.module.css";
import Loading from "../common/HousGlass Loader/Loading";
import { useAudiosByGenre } from "../hooks/useAudiosHooks";

const ReadMoreNonFictionalComponent = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [categoryName, setCategoryName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Fetch all audios for this category
  const { data: responseData, isLoading, isError, error } = useAudiosByGenre(
    categoryId,
    { limit: 100 }
  );

  const [audios, setAudios] = useState([]);
  const [filteredAudios, setFilteredAudios] = useState([]);

  // Process data when it changes
  useEffect(() => {
    if (responseData) {
      if (responseData.data && Array.isArray(responseData.data)) {
        setAudios(responseData.data);
        setFilteredAudios(responseData.data);
      }
      
      if (responseData.category && responseData.category.name) {
        setCategoryName(responseData.category.name);
      }
      
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    }
  }, [responseData, isInitialLoad]);

  // Search audios
  useEffect(() => {
    if (!audios.length) return;
    
    // Apply search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const result = audios.filter(audio => 
        audio.title?.toLowerCase().includes(term) || 
        audio.author?.toLowerCase().includes(term)
      );
      setFilteredAudios(result);
    } else {
      setFilteredAudios(audios);
    }
  }, [searchTerm, audios]);

  // Handler for when a card is clicked
  const handleCardClick = (audio) => {
    navigate(`/audio-play/${audio.slug}`);
  };

  // Handler for back button
  const handleBackClick = () => {
    navigate(-1);
  };

  // Render skeleton loading cards
  const renderSkeletons = () => {
    return Array(12).fill(0).map((_, index) => (
      <div key={`skeleton-${index}`} className={styles.skeletonCard}>
        <div className={styles.skeletonImage}></div>
        <div className={styles.skeletonContent}>
          <div className={styles.skeletonTitle}></div>
          <div className={styles.skeletonAuthor}></div>
        </div>
      </div>
    ));
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button onClick={handleBackClick} className={styles.backButton}>
          ← Back
        </button>
        <h1>{categoryName || "Category"}</h1>
        
        {!isInitialLoad && (
          <div className={styles.searchContainer}>
            <span className={styles.searchIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" 
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search audios..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}
      </div>

      {isLoading && isInitialLoad ? (
        <div className={styles.loadingContainer}>
          <Loading />
        </div>
      ) : isError ? (
        <div className={styles.errorContainer}>
          <div className={styles.error}>
            Error: {error?.message || "Failed to load content"}
          </div>
          <button onClick={handleBackClick} className={styles.backButton}>
            Go Back
          </button>
        </div>
      ) : filteredAudios.length > 0 ? (
        <div className={styles.audioGrid}>
          {isLoading && !isInitialLoad ? renderSkeletons() : (
            filteredAudios.map((audio) => (
              <div
                key={audio.id}
                className={styles.audioCard}
                onClick={() => handleCardClick(audio)}
              >
                {audio.category && (
                  <span className={styles.categoryBadge}>
                    {audio.category}
                  </span>
                )}
                <div className={styles.audioImageContainer}>
                  <img
                    src={audio.mobile_image_url || audio.computer_image_url || '/placeholder-image.jpg'}
                    alt={audio.title}
                    className={styles.audioImage}
                    loading="lazy"
                  />
                </div>
               
              </div>
            ))
          )}
        </div>
      ) : (
        <div className={styles.noContent}>
          {searchTerm ? 
            `No audios found matching "${searchTerm}"` : 
            "No audios available in this category"}
        </div>
      )}
    </div>
  );
};

export default ReadMoreNonFictionalComponent;